<template>
    <div>
        <v-card class="deliveries">
            <v-row>
                <v-col>
                    <Tabs />
                </v-col>
            </v-row>
            <v-toolbar color="white" class="header elevation-0">
                <v-toolbar-title>
                    {{ $t('deliveries.title') }}
                </v-toolbar-title>

                <v-text-field
                    v-model="search"
                    :label="$t('forms.search')"
                    single-line
                    hide-details
                    color="#3200E6"
                    append-icon="mdi-magnify"
                ></v-text-field>

                <v-spacer></v-spacer>

                <div class="actions d-flex">
                    <v-btn v-if="checkAuthorization.create" class="action" @click="createDelivery">
                        {{ $t('deliveries.buttons.create') }}
                        <v-avatar color="white" size="20">
                            <v-icon color="#3200E6">fas fa-plus</v-icon>
                        </v-avatar>
                    </v-btn>
                </div>
            </v-toolbar>
            <v-data-table
                v-if="checkAuthorization.read"
                :headers="headers"
                :items="items"
                :search="search"
                :items-per-page="itemsPerPage"
                :page.sync="page"
                class="elevation-1"
                hide-default-footer
                sort-by="number"
                sort-desc
                multi-sort
                @page-count="pageCount = $event"
            >
                <template v-slot:[`item.actions`]="{ item }">
                    <v-container v-if="item.status">
                        <v-row
                            :class="[
                                'v-data-table-actions',
                                item.status
                                    ? item.status.statusOK === true
                                        ? 'v-data-table-actions-validated'
                                        : 'v-data-table-actions-refused'
                                    : ''
                            ]"
                        >
                            {{
                                item.status.statusOK === true
                                    ? $t('deliveries.content.validated', { date: $d(item.status.timestamp, 'short') })
                                    : $t('deliveries.content.refused', { date: $d(item.status.timestamp, 'short') })
                            }}
                        </v-row>
                        <p v-if="item.status.comment">
                            <br />
                            <small>{{ item.status.comment }}</small>
                        </p>
                    </v-container>
                    <v-container style="width: 115px" fluid v-else>
                        <v-row>
                            <v-col>
                                <v-icon
                                    color="#b9b9b9"
                                    v-if="checkAuthorization.close"
                                    @click="actions(item._id, 'validate', item.number)"
                                >
                                    fas fa-check
                                </v-icon>
                            </v-col>
                            <v-col>
                                <v-icon
                                    color="#b9b9b9"
                                    style="font-size: 1.5em"
                                    @click="actions(item._id, 'refuse', item.number)"
                                    v-if="checkAuthorization.delete"
                                >
                                    fas fa-trash
                                </v-icon>
                            </v-col>
                        </v-row>
                    </v-container>
                </template>
            </v-data-table>

            <v-row class="align-center" style="margin-top: 0">
                <v-col class="button-delivery">
                    <vue-excel-xlsx :data="items" :columns="headers_xls" :filename="'filename'" :sheetname="'sheetname'">
                        <v-btn class="export">
                            {{ $t('forms.export') }}
                            <v-icon> fas fa-download</v-icon>
                        </v-btn>
                    </vue-excel-xlsx>
                </v-col>
                <v-col>
                    <v-pagination
                        v-if="items.length > itemsPerPage"
                        color="#3200E6"
                        v-model="page"
                        :length="pageCount"
                        :total-visible="7"
                    />
                </v-col>
                <v-col />
            </v-row>
        </v-card>

        <v-dialog v-model="dialog.display" max-width="500">
            <v-card>
                <v-card-title class="headline" v-if="dialog.action === 'validate'">
                    {{ $t('deliveries.dialog.validateTitle') }}
                </v-card-title>
                <v-card-title class="headline" v-else>
                    {{ $t('deliveries.dialog.refuseTitle') }}
                </v-card-title>

                <v-card-subtitle class="subtitle" v-if="dialog.action === 'validate'">
                    {{ $t('deliveries.dialog.validate', { number: this.dialog.number }) }}
                </v-card-subtitle>
                <v-card-subtitle class="subtitle" v-else>
                    {{ $t('deliveries.dialog.refuse', { number: this.dialog.number }) }}
                </v-card-subtitle>

                <v-card-text>
                    <v-textarea :label="$t('forms.comment')" v-model="dialog.comment" />
                </v-card-text>

                <v-card-actions>
                    <v-btn class="action" text @click="validate()">
                        <span v-if="dialog.action === 'validate'">
                            {{ $t('forms.validate') }}
                        </span>
                        <span v-else>
                            {{ $t('forms.refuse') }}
                        </span>
                    </v-btn>

                    <v-btn class="cancel" text @click="dialog.display = false">
                        {{ $t('forms.cancel') }}
                    </v-btn>

                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="createForm" max-width="800">
            <v-card>
                <v-card-title class="headline">
                    {{ $t('deliveries.buttons.create') }}
                </v-card-title>
                <CreateDelivery v-on:removeForm="removeFormFromView" :type="$route.query.tab" :opened="createForm" />
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar.display" :timeout="snackbar.timeout">
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn color="pink" text v-bind="attrs" @click="hideNotification()">
                    {{ $t('forms.errors.close') }}
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { api } from '@/utils/api';
import { mapMutations, mapGetters } from 'vuex';
import moment from 'moment';
import { formatDate } from '@/utils/helpers';
import Tabs from '@/components/Tabs';
import CreateDelivery from '@/components/CreateDelivery';

export default {
    name: 'Home',
    components: {
        Tabs,
        CreateDelivery
    },
    data() {
        return {
            page: 1,
            pageCount: 0,
            itemsPerPage: 10,
            types: ['NIN', 'NCN'],
            search: '',
            dialog: {
                display: false,
                number: '',
                comment: '',
                action: ''
            },
            createForm: false,
            activeIndex: null,
            deliveries: [],
            snackbar: {
                display: false,
                text: '',
                timeout: -1
            }
        };
    },
    computed: {
        ...mapGetters(['getClientName', 'getServiceName']),
        checkAuthorization() {
            return {
                read: this.$store.state.groups.userRoles?.deliveries?.read_user || this.$store.state.groups.authorization?.admin,
                create: this.$store.state.groups.userRoles?.deliveries?.open_user || this.$store.state.groups.authorization?.admin,
                close: this.$store.state.groups.userRoles?.deliveries?.close_user || this.$store.state.groups.authorization?.admin,
                delete: this.$store.state.groups.userRoles?.deliveries?.delete_user || this.$store.state.groups.authorization?.admin
            };
        },
        headers() {
            const sort = (a, b) => new Date(b).valueOf() - new Date(a).valueOf();
            return [
                { text: this.$t('deliveries.header.number'), value: 'number', align: 'center' },
                { text: this.$t('deliveries.header.product'), value: 'product', align: 'center' },
                { text: this.$t('deliveries.header.group'), value: 'group', align: 'center' },
                { text: this.$t('deliveries.header.ASN'), value: 'ASN', align: 'center' },
                { text: this.$t('deliveries.header.PO'), value: 'PO', align: 'center' },
                { text: this.$t('deliveries.header.origin'), value: 'origin', align: 'center' },
                {
                    text: this.$t('deliveries.header.estimated_reception_date'),
                    value: 'estimated_reception_date',
                    align: 'center',
                    sort
                },
                { text: this.$t('deliveries.header.creation_date'), value: 'creation_date', align: 'center', sort },
                { text: this.$t('deliveries.header.author'), value: 'author', align: 'center' },
                { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
            ];
        },
        headers_xls() {
            return [
                { label: 'Number', field: 'number' },
                { label: 'Product', field: 'product' },
                { label: 'Group', field: 'group' },
                { label: 'ASN', field: 'ASN' },
                { label: 'PO', field: 'PO' },
                { label: 'Origin', field: 'origin' },
                { label: 'Estimated reception date', field: 'estimated_reception_date' },
                { label: 'Creation date', field: 'creation_date' },
                { label: 'Author', field: 'author' }
            ];
        },
        items() {
            if (!this.deliveries || this.deliveries.length === 0) return [];
            const deliveries = this.deliveries.filter(delivery => delivery.deleted === 0);
            deliveries.forEach(e => (e.estimated_reception_date = moment(e.estimated_reception_date).format('ll')));
            deliveries.forEach(e => (e.creation_date = moment(e.creation_timestamp).format('ll')));
            return deliveries;
        }
    },
    methods: {
        ...mapMutations('deliveries', ['setCreatedDate', 'setCreatedDateString']),
        async validate(id, action) {
            const body = {
                comment: this.dialog.comment,
                clientName: this.getClientName,
                serviceName: this.getServiceName
            };
            const json = await api.patch('validate', { id: this.dialog.id, action: this.dialog.action }, body);
            const delivery = process.env.NODE_ENV === 'development' && process.env.DATA_SOURCE === 'mock' ? json.delivery : json;

            for (let index = 0; index < this.deliveries.length; index++) {
                if (this.deliveries[index]._id === this.dialog.id) {
                    this.deliveries.splice(index, 1, delivery);
                    break;
                }
            }
            if (delivery.error) {
                this.snackbar.text = delivery.error;
            }
            this.dialog.display = false;
            this.snackbar.display = true;
        },
        actions(id, action, number) {
            this.snackbar.text = this.$t(`deliveries.snackbar.${action}d`, { number });
            this.dialog.comment = '';
            this.dialog.number = number;
            this.dialog.id = id;
            this.dialog.action = action;
            this.dialog.display = true;
        },
        createDelivery() {
            this.setCreatedDate(new Date().toISOString().substr(0, 10));
            this.setCreatedDateString(formatDate(new Date().toISOString().substr(0, 10)));
            this.createForm = true;
        },
        removeFormFromView(delivery) {
            this.deliveries.push(delivery);
            this.createForm = false;
        },
        hideNotification() {
            if (this.snackbar.display) {
                this.snackbar.display = false;
            }
        },
        async changeType() {
            const deliveries = this.$store.state.groups.userRoles?.deliveries;

            let tab;
            if (deliveries) {
                if (this.$route.query.tab && this.types.includes(this.$route.query.tab)) {
                    tab = deliveries[`${this.$route.query.tab}_user`] && this.$route.query.tab;
                }
                if (!tab) {
                    let i;
                    for (i = 0; i < this.types.length && deliveries[`${this.types[i]}_user`] === false; i += 1);
                    tab = this.types[i] || 'NIN';
                }
            } else {
                return;
            }
            if (!this.$route.query.tab || this.$route.query.tab !== tab) {
                this.$router.replace({ path: this.$route.path, query: { tab } });
            }
            try {
                this.deliveries = await api.get('deliveries', { type: this.$route.query.tab });
            } catch (err) {
                this.deliveries = [];
            }
        }
    },
    async created() {
        await this.changeType();
    },
    watch: {
        async '$route.query.tab'() {
            await this.changeType();
        },
        async '$store.state.groups.connected'() {
            await this.changeType();
        }
    }
};
</script>

<style lang="scss">
.v-data-table-actions {
    font-weight: 700;
}

.v-data-table-actions-validated {
    font-family: 'IBM Plex Mono', monospace;
    color: #575756;
}

.v-data-table-actions-refused {
    color: #f44336;
}

.v-data-table-export-excel {
    margin-left: 10px;
}

.table-footer-prepend {
    margin-top: -58px;
    height: 58px;
}

.v-text-field {
    margin-left: 15px;
    width: 200px;
}

.deliveries {
    .header {
        .actions {
            .v-btn {
                margin-left: 20px;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.button-delivery {
    margin: 16px 0 16px 20px;
}
.v-textarea {
    width: 400px;
}

.v-card__subtitle {
    margin-top: 10px;
}

.action {
    margin: 0 10px 10px 20px;
}

.cancel {
    margin: 0 10px 10px 20px;
}

.v-avatar {
    margin-left: 5px;
    .v-icon {
        font-size: 0.9em;
    }
}

.col.export {
    margin-left: 16px;
}
</style>
